import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import moment from 'moment-timezone';

import type { CallerPartnerBudgetInputType } from '@/gql/graphql';
import { SubsidyAllowanceKind } from '@/gql/graphql';
import type { SubsidyAllowanceBalanceCreateInput, SubsidyAllowanceBalanceUpdateInput } from '@/models/PartnerMemberAllowanceBalances';
import { fetchSubsidyAllowanceBalances } from '@/services/partner-subsidy/fetchSubsidyAllowanceBalances';
import { fetchSubsidyAllowancesByPartner } from '@/services/partner-subsidy/fetchSubsidyAllowancesByPartner';
import { useDashboard } from '@/use/app';
import { usePartner } from '@/use/partner';
import util from '@/Utils/util';
import { buildAllowanceFormState, getBalanceRowError, isRidesAllowanceKind } from '../Partners/Subsidy/budgetBalanceForm';
import {
  buildBlankBudgetRow,
  buildEmptyBudgetChanges,
  isBlankBudgetRow,
  toBalanceFormRow,
  type EditableBudgetGroup,
  type EditableBudgetRow,
  type PartnerBudgetBalancePatch,
  type PartnerMemberBudgetChanges,
  type PartnerMemberBudgets,
} from './partnerMemberBudget';

type UseCallerPartnerBudgetsOptions = {
  budgets: Ref<PartnerMemberBudgets>;
  changes: Ref<PartnerMemberBudgetChanges>;
  callerId: MaybeRefOrGetter<string | null>;
  partnerCodeName: MaybeRefOrGetter<string>;
};

const budgetFieldFromKind = (kind: string) => (isRidesAllowanceKind(kind) ? 'budgetRides' : 'budgetCredits');
const preAuthFieldFromKind = (kind: string) =>
  isRidesAllowanceKind(kind) ? 'budgetRidesPreAuthorizationNumber' : 'budgetCreditsPreAuthorizationNumber';

const buildPlaceholderGroup = (kind: SubsidyAllowanceKind, allowanceName: string): EditableBudgetGroup => ({
  allowanceId: 0,
  allowanceName,
  kind,
  isBudget: true,
  budgetLimit: null,
  isPreAuthorizationNumberRequired: false,
  balances: [buildBlankBudgetRow()],
});

const toBalanceInput = (row: EditableBudgetRow) => {
  const { amount, startBudgetAmount, beginningDate, suspensionDate, preAuthorizationNumber } = toBalanceFormRow(row);
  return { amount, startBudgetAmount, beginningDate, suspensionDate, preAuthorizationNumber: preAuthorizationNumber.trim() || null };
};

export const useCallerPartnerBudgets = ({ budgets, changes, callerId, partnerCodeName }: UseCallerPartnerBudgetsOptions) => {
  const { isPartnerDashboard } = useDashboard();
  const { partnerInfo, isPartnerFieldVisible, isPartnerFieldRequired, getPartnerFieldLabel } = usePartner();

  const isLoading = ref(false);
  const loadError = ref('');

  let latestLoadId = 0;

  const currentDate = () => moment.tz(partnerInfo.value?.timeZone || 'UTC').format('YYYY-MM-DD');

  const groupTitle = (group: EditableBudgetGroup) =>
    getPartnerFieldLabel({
      field: budgetFieldFromKind(group.kind),
      defaultValue: isRidesAllowanceKind(group.kind) ? 'Rides budgets' : 'Credits budgets',
      required: false,
    });

  const visibleGroups = computed(() => budgets.value.groups.filter((group) => isPartnerFieldVisible(budgetFieldFromKind(group.kind))));

  const groupError = (group: EditableBudgetGroup) => {
    const filled = group.balances.filter((row) => !isBlankBudgetRow(row));
    const preAuthField = preAuthFieldFromKind(group.kind);
    const preAuthVisible = isPartnerFieldVisible(preAuthField);
    const isPreAuthRequired = preAuthVisible && (isPartnerFieldRequired(preAuthField) || group.isPreAuthorizationNumberRequired);
    const formGroup = {
      ...group,
      isPreAuthorizationNumberRequired: group.isPreAuthorizationNumberRequired && preAuthVisible,
      balances: filled.map(toBalanceFormRow),
    };
    for (const row of filled) {
      if (row.amount == null) {
        return `${groupTitle(group)} amount is required.`;
      }
      if (isPreAuthRequired && !(row.preAuthorizationNumber ?? '').trim()) {
        return `${groupTitle(group)} pre-authorization number is required.`;
      }
      const error = getBalanceRowError(formGroup, toBalanceFormRow(row), currentDate());
      if (error) {
        return error;
      }
    }
    return '';
  };

  const budgetError = computed(() => {
    if (isLoading.value) {
      return 'Budgets are still loading.';
    }
    if (loadError.value) {
      return loadError.value;
    }
    for (const group of visibleGroups.value) {
      const error = groupError(group);
      if (error) {
        return error;
      }
    }
    return '';
  });

  const partnerBudgetInput = computed((): CallerPartnerBudgetInputType | null => {
    const input: CallerPartnerBudgetInputType = {};
    for (const group of visibleGroups.value) {
      const row = group.balances.find((item) => item.id == null && !isBlankBudgetRow(item));
      if (!row) {
        continue;
      }
      const { amount, beginningDate, suspensionDate, preAuthorizationNumber } = toBalanceInput(row);
      input[isRidesAllowanceKind(group.kind) ? 'rides' : 'credits'] = { amount, beginningDate, suspensionDate, preAuthorizationNumber };
    }
    return input.rides || input.credits ? input : null;
  });

  const balanceChanges = computed(() => {
    const createdBalances: SubsidyAllowanceBalanceCreateInput[] = [];
    const updatedBalances: SubsidyAllowanceBalanceUpdateInput[] = [];
    for (const group of visibleGroups.value) {
      for (const row of group.balances) {
        if (row.id == null) {
          if (!isBlankBudgetRow(row)) {
            createdBalances.push({ subsidyAllowanceId: group.allowanceId, ...toBalanceInput(row) });
          }
        } else if (changes.value.updatedIds.includes(row.id)) {
          updatedBalances.push({ id: row.id, ...toBalanceInput(row) });
        }
      }
    }
    return { createdBalances, updatedBalances, removedBalanceIds: [...changes.value.removedIds] };
  });

  const hasBalanceChanges = computed(() => {
    const { createdBalances, updatedBalances, removedBalanceIds } = balanceChanges.value;
    return Boolean(createdBalances.length || updatedBalances.length || removedBalanceIds.length);
  });

  const applyAllowancesToPlaceholders = (groups: EditableBudgetGroup[]) => {
    for (const group of groups) {
      const placeholder = budgets.value.groups.find((item) => item.kind === group.kind);
      if (placeholder) {
        Object.assign(placeholder, { ...group, balances: placeholder.balances });
      } else {
        budgets.value.groups.push({ ...group, balances: [buildBlankBudgetRow()] });
      }
    }
  };

  const loadBudgets = async () => {
    const loadId = ++latestLoadId;
    const currentCallerId = toValue(callerId);
    const currentPartnerCodeName = toValue(partnerCodeName);

    changes.value = buildEmptyBudgetChanges();
    loadError.value = '';
    budgets.value = {
      groups: currentCallerId
        ? []
        : [
            buildPlaceholderGroup(SubsidyAllowanceKind.BudgetRides, 'Rides budgets'),
            buildPlaceholderGroup(SubsidyAllowanceKind.BudgetCredits, 'Credits budgets'),
          ],
    };
    if (!isPartnerDashboard.value || !currentPartnerCodeName) {
      isLoading.value = false;
      return;
    }

    isLoading.value = Boolean(currentCallerId);
    try {
      const [allowances, page] = await Promise.all([
        fetchSubsidyAllowancesByPartner({ partnerCodeName: currentPartnerCodeName }),
        currentCallerId
          ? fetchSubsidyAllowanceBalances({
              partnerCodeName: currentPartnerCodeName,
              callerId: currentCallerId,
              limit: 1,
              latestPastBudgetCount: 1,
            })
          : null,
      ]);
      if (loadId !== latestLoadId) {
        return;
      }
      const member = page?.members.find((item) => item.callerId === currentCallerId);
      const { groups } = buildAllowanceFormState({ allowances, balances: member?.balances ?? [], budgetOnly: true });
      if (!currentCallerId) {
        applyAllowancesToPlaceholders(groups);
        return;
      }
      budgets.value = {
        groups: groups.map((group) => ({
          ...group,
          balances: group.balances.length ? group.balances : [buildBlankBudgetRow()],
        })),
      };
    } catch (error) {
      // Without a caller, createCaller still sends the new balances and reports any missing allowance.
      if (loadId === latestLoadId && currentCallerId) {
        loadError.value = util.getGraphqlError(error, false);
      }
    } finally {
      if (loadId === latestLoadId) {
        isLoading.value = false;
      }
    }
  };

  const updateBalance = (balance: EditableBudgetRow, patch: PartnerBudgetBalancePatch) => {
    Object.assign(balance, patch);
    if (balance.id != null && !changes.value.updatedIds.includes(balance.id)) {
      changes.value.updatedIds.push(balance.id);
    }
  };

  const removeBalance = (group: EditableBudgetGroup, balance: EditableBudgetRow) => {
    const { id } = balance;
    if (id != null) {
      changes.value.updatedIds = changes.value.updatedIds.filter((item) => item !== id);
      if (!changes.value.removedIds.includes(id)) {
        changes.value.removedIds.push(id);
      }
    }
    group.balances = group.balances.filter((row) => row.key !== balance.key);
    if (!group.balances.length) {
      group.balances.push(buildBlankBudgetRow());
    }
  };

  watch(
    () => [isPartnerDashboard.value, toValue(callerId), toValue(partnerCodeName)] as const,
    () => loadBudgets(),
    { immediate: true },
  );

  return {
    isLoading,
    loadError,
    visibleGroups,
    budgetError,
    partnerBudgetInput,
    balanceChanges,
    hasBalanceChanges,
    loadBudgets,
    updateBalance,
    removeBalance,
  };
};
